<template>
  <v-card class="history-panel" elevation="0">
    <div class="history-panel__heading">
      <div><h2>Movement History</h2><p>Every stock change remains attributable and auditable.</p></div>
      <div class="history-panel__filters">
        <v-select v-model="typeFilter" :items="movementTypes" item-title="label" item-value="value" label="Movement type" clearable density="compact" hide-details variant="outlined" @update:modelValue="loadMovements"></v-select>
        <v-btn icon="mdi-refresh" variant="text" @click="loadMovements"></v-btn>
      </div>
    </div>
    <div v-if="loading" class="history-panel__empty"><v-progress-circular indeterminate color="primary"></v-progress-circular></div>
    <div v-else-if="!movements.length" class="history-panel__empty"><v-icon size="34" color="primary">mdi-history</v-icon><strong>No movements yet</strong><p>Opening stock, receipts and count corrections will appear here.</p></div>
    <div v-else class="history-table-wrap">
      <table class="history-table">
        <thead><tr><th>Date</th><th>Item</th><th>Type</th><th>Reference</th><th>Reason</th><th class="number-cell">Change</th><th class="number-cell">Stock After</th><th v-if="showCosts" class="number-cell">Cost</th><th class="number-cell">Price</th><th v-if="canEdit" class="action-cell">Actions</th></tr></thead>
        <tbody>
          <tr v-for="movement in movements" :key="movement.id">
            <td>{{ formatDateTime(movement.created_at) }}</td>
            <td><strong>{{ movement.item_name }}</strong><span>{{ movement.category_name || 'Uncategorized' }}</span></td>
            <td><v-chip :color="typeColor(movement.movement_type)" size="small" variant="tonal">{{ formatType(movement.movement_type) }}</v-chip></td>
            <td>{{ referenceLabel(movement) }}</td>
            <td>{{ movement.reason || '—' }}</td>
            <td :class="['number-cell', 'quantity-change', Number(movement.quantity_change) >= 0 ? 'positive' : 'negative']">{{ formatChange(movement.quantity_change) }}</td>
            <td class="number-cell">{{ movement.quantity_after }}</td>
            <td v-if="showCosts" class="number-cell">{{ formatCurrency(movement.unit_cost) }}</td>
            <td class="number-cell">{{ formatCurrency(movement.unit_price) }}</td>
            <td v-if="canEdit" class="action-cell">
              <v-btn
                v-if="Number(movement.can_edit_restock) === 1"
                icon="mdi-pencil-outline"
                size="x-small"
                title="Edit this restock"
                variant="text"
                @click="$emit('edit-restock', movement)"
              ></v-btn>
              <span v-else>—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </v-card>
</template>

<script>
import secureAxios from '@/utils/secureAxios';

export default {
  name: 'InventoryHistory',
  props: {
    refreshKey: { type: Number, default: 0 },
    showCosts: { type: Boolean, default: true },
    canEdit: { type: Boolean, default: false },
  },
  emits: ['edit-restock'],
  data() {
    return {
      movements: [], loading: false, typeFilter: null,
      movementTypes: [
        { label: 'Opening Stock', value: 'opening' }, { label: 'Receipt', value: 'receipt' },
        { label: 'Model Sale', value: 'model_sale' }, { label: 'Manual Add', value: 'manual_add' },
        { label: 'Direct Sale', value: 'direct_sale' },
        { label: 'Manual Remove', value: 'manual_remove' }, { label: 'Damage', value: 'damage' },
        { label: 'Loss', value: 'loss' }, { label: 'Internal Use', value: 'internal_use' },
        { label: 'Count Correction', value: 'count_correction' }, { label: 'Return', value: 'return' },
      ],
    };
  },
  watch: { refreshKey() { this.loadMovements(); } },
  mounted() { this.loadMovements(); },
  methods: {
    async loadMovements() {
      this.loading = true;
      try { const response = await secureAxios.get('/api/inventory/movements', { params: { type: this.typeFilter || undefined } }); this.movements = response.data || []; }
      catch { this.movements = []; }
      finally { this.loading = false; }
    },
    formatType(value) { return String(value || '').replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase()); },
    formatChange(value) { const number = Number(value || 0); return `${number > 0 ? '+' : ''}${number}`; },
    typeColor(type) { if (['receipt', 'opening', 'manual_add', 'return'].includes(type)) return 'success'; if (['damage', 'loss', 'manual_remove'].includes(type)) return 'error'; if (type === 'count_correction') return 'warning'; return 'primary'; },
    referenceLabel(movement) { if (movement.order_id) return `Order #${movement.order_id}`; if (movement.count_session_id) return `Count #${movement.count_session_id}`; if (movement.buyer_name) return `${movement.buyer_name}${movement.payment_method ? ` · ${movement.payment_method}` : ''}`; if (movement.period_name) return movement.period_name; return '—'; },
    formatDateTime(value) { return value ? new Intl.DateTimeFormat('en-CA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '—'; },
    formatCurrency(value) { return value === null || value === undefined ? '—' : new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(value)); },
  },
};
</script>

<style scoped>
.history-panel { border: 1px solid #dfe7f0; border-radius: 10px; overflow: hidden; }
.history-panel__heading { display: flex; align-items: center; justify-content: space-between; gap: 20px; padding: 20px 22px; border-bottom: 1px solid #edf1f5; }
.history-panel h2 { margin: 0; color: #173553; font-size: 17px; }
.history-panel p { margin: 4px 0 0; color: #6b7f93; font-size: 13px; }
.history-panel__filters { display: flex; align-items: center; gap: 6px; min-width: 270px; }
.history-panel__filters .v-select { flex: 1; }
.history-panel__empty { display: flex; min-height: 300px; flex-direction: column; align-items: center; justify-content: center; gap: 8px; text-align: center; }
.history-table-wrap { overflow-x: auto; }
.history-table { width: 100%; border-collapse: collapse; }
.history-table th, .history-table td { padding: 13px 14px; border-bottom: 1px solid #edf1f5; color: #40536a; font-size: 12px; text-align: left; white-space: nowrap; }
.history-table th { background: #f8fafc; color: #6b7f93; font-size: 10px; letter-spacing: .06em; text-transform: uppercase; }
.history-table td:nth-child(2) { display: flex; min-width: 180px; flex-direction: column; }
.history-table td span { color: #7d8b99; font-size: 11px; }
.number-cell { text-align: right !important; }
.action-cell { width: 58px; text-align: center !important; }
.quantity-change { font-weight: 700; }
.quantity-change.positive { color: #27845b; }
.quantity-change.negative { color: #c34d4d; }
@media (max-width: 700px) { .history-panel__heading { align-items: stretch; flex-direction: column; } .history-panel__filters { min-width: 0; } }
</style>
